/**
 * Keeps the map camera centered on a selected vehicle while follow mode is active.
 * Camera updates are driven by the vehicle custom layer frame callback (same position as the sprite).
 */

import maplibregl from "maplibre-gl";
import { useCallback, useEffect, useRef } from "react";
import { type VehicleCustomLayer } from "../components/MapLibre/VehicleCustomLayer";
import { isValidVehicleSelectionLngLat } from "../components/MapLibre/vehicleSelection/vehicleSelection.types";
import {
    ephemeralStore,
    getMapInstance,
    mapReadySlice,
    navigationSessionSlice,
    useEphemeralSelector,
} from "../store/ephemeral";

/** Minimum screen-space delta (px) before jumpTo; avoids redundant camera work when idle. */
const VEHICLE_FOLLOW_MIN_JUMP_PX = 4;

/** Pressed-pointer displacement (px) beyond which a touch/click counts as a drag gesture. */
const VEHICLE_FOLLOW_GESTURE_CLICK_TOLERANCE_PX = 3;

export interface UseMapVehicleFollowParams {

    /**
     * Latest coordinates published for the selected vehicle (the same values
     * the vehicle layer pushes to React each frame). Drives the camera while
     * follow is active — independent of the layer frame-callback binding.
     */
    "followLngLat": [number, number] | null;

    /**
     * Whether a vehicle is currently selected (regardless of published
     * coordinates). Follow must survive the frames where the selection exists
     * but its position has not been published yet.
     */
    "hasSelection": boolean;

    /**
     * Bottom viewport inset (px) occupied by UI (e.g. the itinerary panel).
     * The followed vehicle is centered in the visible area above it.
     */
    "followBottomInsetPx"?: number;
}

export interface UseMapVehicleFollowResult {

    /**
     * Wires frame-synced follow on a vehicle custom layer instance.
     * Must be called whenever a new layer is created (initial load or style switch).
     *
     * @param layer Vehicle custom layer instance, or undefined to skip.
     */
    "bindVehicleFollowLayer": ( layer: VehicleCustomLayer | undefined ) => void;
}

/**
 * Clears vehicle follow mode in the navigation session slice.
 */
function clearVehicleFollow(): void {
    ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
        if ( !previous.isFollowingVehicle ) {
            return previous;
        }

        return {
            ...previous,
            "isFollowingVehicle": false,
        };
    } );
}

/**
 * Returns whether the vehicle should be re-centered on screen.
 *
 * Compares the vehicle's projected screen position with the center of the
 * visible (bottom-padded) viewport rather than the raw camera center: with a
 * bottom inset, `getCenter()` legitimately differs from the vehicle position
 * and a degree-based deadband would jump every frame.
 *
 * @param map Target map instance.
 * @param center Candidate vehicle position.
 * @param bottomInsetPx Bottom viewport inset (px) reserved for UI panels.
 * @returns True when the vehicle is off the padded-viewport center beyond the deadband.
 */
function shouldJumpMapCenter( map: maplibregl.Map, center: [number, number], bottomInsetPx: number ): boolean {
    const container = map.getContainer();
    if ( container.clientWidth === 0 || container.clientHeight === 0 ) {
        return false;
    }

    const projected = map.project( new maplibregl.LngLat( center[0], center[1] ) );
    const targetX = container.clientWidth / 2;
    const targetY = ( container.clientHeight - bottomInsetPx ) / 2;

    return Math.hypot( projected.x - targetX, projected.y - targetY ) > VEHICLE_FOLLOW_MIN_JUMP_PX;
}

/**
 * Registers frame-synced camera lock and user-gesture listeners for vehicle follow mode.
 *
 * @param params Hook parameters.
 * @returns Layer binding handle for MapComponent lifecycle hooks.
 */
export function useMapVehicleFollow( params: UseMapVehicleFollowParams ): UseMapVehicleFollowResult {
    const { followLngLat, hasSelection, followBottomInsetPx = 0 } = params;

    const isMapReady = useEphemeralSelector( mapReadySlice, ( ready ) => ready.isMapReady );
    const isFollowingVehicle = useEphemeralSelector(
        navigationSessionSlice,
        ( session ) => session.isFollowingVehicle,
    ) === true;

    const boundLayerRef = useRef<VehicleCustomLayer | undefined>( undefined );
    const followBottomInsetRef = useRef( followBottomInsetPx );

    useEffect( () => {
        followBottomInsetRef.current = followBottomInsetPx;
    }, [ followBottomInsetPx ] );

    /**
     * Jumps the map so the vehicle is centered in the visible (bottom-padded)
     * viewport; zoom, bearing, and pitch are preserved by MapLibre.
     *
     * @param center Target center coordinates.
     */
    const jumpMapCenterOnly = useCallback( ( center: [number, number] ): void => {
        const activeMap = getMapInstance();
        const bottomInsetPx = followBottomInsetRef.current;
        if ( activeMap === undefined || !shouldJumpMapCenter( activeMap, center, bottomInsetPx ) ) {
            return;
        }

        activeMap.jumpTo( {
            "center":  new maplibregl.LngLat( center[0], center[1] ),
            "padding": {
                "top":    0,
                "right":  0,
                "bottom": bottomInsetPx,
                "left":   0,
            },
        } );
    }, [] );

    const bindVehicleFollowLayer = useCallback( ( layer: VehicleCustomLayer | undefined ): void => {
        if ( boundLayerRef.current !== undefined ) {
            boundLayerRef.current.onSelectedVehicleFramePosition = null;
            boundLayerRef.current = undefined;
        }

        if ( layer === undefined ) {
            return;
        }

        boundLayerRef.current = layer;
        layer.onSelectedVehicleFramePosition = ( lngLat: [number, number] ): void => {
            const following = ephemeralStore.peek( navigationSessionSlice ).isFollowingVehicle === true;
            if ( !following || !isValidVehicleSelectionLngLat( lngLat ) ) {
                return;
            }

            jumpMapCenterOnly( lngLat );
        };
    }, [ jumpMapCenterOnly ] );

    /*
     * Camera follow driven by the published selection coordinates. This React
     * channel does not depend on the layer frame callback: it fires on every
     * published position while follow is active, covers the initial snap when
     * follow is toggled on, and engages as soon as the first valid position
     * arrives (e.g. a trip not yet departed when it was selected).
     */
    useEffect( () => {
        if (
            !isFollowingVehicle ||
            followLngLat === null ||
            !isValidVehicleSelectionLngLat( followLngLat )
        ) {
            return;
        }

        jumpMapCenterOnly( followLngLat );
    }, [ followLngLat, isFollowingVehicle, jumpMapCenterOnly ] );

    useEffect( () => () => {
        bindVehicleFollowLayer( undefined );
    }, [ bindVehicleFollowLayer ] );

    /*
     * Drop follow only when the vehicle is actually deselected. A transient
     * missing position (first frames after programmatic selection, or a trip
     * not yet departed) must not cancel follow — the frame callback starts
     * centering as soon as a position becomes available.
     */
    useEffect( () => {
        if ( !hasSelection && isFollowingVehicle ) {
            clearVehicleFollow();
        }
    }, [ hasSelection, isFollowingVehicle ] );

    /*
     * Stop follow on any user camera interaction, whatever the input means.
     * Raw DOM gestures are the primary signal — they fire instantly, before
     * MapLibre emits camera events (which previously let follow keep fighting
     * a pinch until thresholds were reached):
     *   - wheel               → zoom intent
     *   - second touch point  → pinch / two-finger rotate-or-pitch intent
     *   - pressed-pointer displacement past the click tolerance → drag intent
     *     (a plain tap stays under the tolerance and keeps follow alive)
     *   - dblclick            → zoom intent
     * The MapLibre `movestart` event filtered on `originalEvent` is the
     * catch-all covering keyboard navigation and any gesture path missed by
     * the DOM listeners. Programmatic camera updates (jumpTo/easeTo/fitBounds)
     * never carry `originalEvent` and never produce DOM input events, so
     * auto-follow can never cancel itself.
     */
    useEffect( () => {
        const map = getMapInstance();
        if ( map === undefined || !isMapReady ) {
            return;
        }

        const stopFollow = (): void => {
            if ( ephemeralStore.peek( navigationSessionSlice ).isFollowingVehicle ) {
                clearVehicleFollow();
            }
        };

        const canvasContainer = map.getCanvasContainer();
        const pressedPointerStart = new Map<number, {
            "x": number;
            "y": number;
        }>();

        const handlePointerDown = ( event: PointerEvent ): void => {
            pressedPointerStart.set( event.pointerId, {
                "x": event.clientX,
                "y": event.clientY,
            } );

            if ( pressedPointerStart.size > 1 ) {
                stopFollow();
            }
        };

        const handlePointerMove = ( event: PointerEvent ): void => {
            const start = pressedPointerStart.get( event.pointerId );
            if ( start === undefined ) {
                return;
            }

            if (
                Math.hypot( event.clientX - start.x, event.clientY - start.y ) >
                VEHICLE_FOLLOW_GESTURE_CLICK_TOLERANCE_PX
            ) {
                pressedPointerStart.delete( event.pointerId );
                stopFollow();
            }
        };

        const handlePointerRelease = ( event: PointerEvent ): void => {
            pressedPointerStart.delete( event.pointerId );
        };

        const handleMapMoveStart = ( event: maplibregl.MapLibreEvent ): void => {
            if ( event.originalEvent !== undefined ) {
                stopFollow();
            }
        };

        canvasContainer.addEventListener( "pointerdown", handlePointerDown, { "passive": true } );
        canvasContainer.addEventListener( "pointermove", handlePointerMove, { "passive": true } );
        canvasContainer.addEventListener( "pointerup", handlePointerRelease, { "passive": true } );
        canvasContainer.addEventListener( "pointercancel", handlePointerRelease, { "passive": true } );
        canvasContainer.addEventListener( "wheel", stopFollow, { "passive": true } );
        canvasContainer.addEventListener( "dblclick", stopFollow, { "passive": true } );
        map.on( "movestart", handleMapMoveStart );

        return () => {
            canvasContainer.removeEventListener( "pointerdown", handlePointerDown );
            canvasContainer.removeEventListener( "pointermove", handlePointerMove );
            canvasContainer.removeEventListener( "pointerup", handlePointerRelease );
            canvasContainer.removeEventListener( "pointercancel", handlePointerRelease );
            canvasContainer.removeEventListener( "wheel", stopFollow );
            canvasContainer.removeEventListener( "dblclick", stopFollow );
            map.off( "movestart", handleMapMoveStart );
        };
    }, [ isMapReady ] );

    return { bindVehicleFollowLayer };
}
